import type CreativeSDK from '@cesdk/cesdk-js';
import { GQLDesignPageFragment, GQLProductFragment } from '@theprintbar/graphql';
import { Merch, getDesignReadySides, getSortedColours } from '@theprintbar/shared';
import { getBackground, getPage } from './block';
import { createPageForSide, getPageSize } from './createPageForSide';
import { resetTeamNameAssets } from './customisation/assets/teamNames';
import { addExclusionZonesToPage } from './exclusionZones';
import { ensureGraphicBlocksHaveCorrectURLs } from './graphic';
import { addNeckLabelBlocks } from './neckLabelBlocks';
import { addBackgroundToPage } from './page';

interface LoadFromSceneProps {
	editor: CreativeSDK;
	product: GQLProductFragment;
	domain: string;
	merch?: Merch;
	content?: GQLDesignPageFragment;
}

export async function loadFromScene({ editor, product, merch, domain, content }: LoadFromSceneProps) {
	if (!merch?.scene) throw 'No scene found';
	const { block } = editor.engine;

	const merchSides = getDesignReadySides(product);
	let scene: number;

	const colour = merch?.colour || getSortedColours(product.colours)[0].colour.code;

	const sceneIsUrl = merch.scene.startsWith('http');
	scene = sceneIsUrl ? await editor.engine.scene.loadFromURL(merch.scene) : await editor.engine.scene.loadFromString(merch.scene);
	await new Promise(res => setTimeout(res, 10));
	// Sequential (not `forEach(async…)`): `addBackgroundToPage` reads `getGlobalBoundingBoxX(page)`
	// which needs the scene's auto-layout of sibling pages to be settled. Running iterations in
	// parallel caused intermittent off-page backgrounds when loading saved scenes.
	for (const printSide of merchSides) {
		try {
			const background = getBackground(editor, printSide.side);
			if (background) {
				block.destroy(background);
			}

			let page = getPage(editor, printSide.side);
			if (!page) {
				page = createPageForSide(editor, scene, product, printSide, colour);
			}

			const { width, height, scaleFactor } = getPageSize(printSide);
			editor.engine.block.setWidth(page, width);
			editor.engine.block.setHeight(page, height);
			editor.engine.block.setMetadata(page, 'scaleFactor', scaleFactor.toString());

			await new Promise(res => setTimeout(res, 10));
			addBackgroundToPage(editor, domain, scene, page, product, printSide, colour);
			addExclusionZonesToPage(editor, printSide, page);
		} catch (e) {
			console.warn(`[loadFromScene] Failed to prepare side ${printSide.side}:`, e);
		}
	}

	await addNeckLabelBlocks(editor, product);
	await ensureGraphicBlocksHaveCorrectURLs(editor, domain);

	await new Promise(res => setTimeout(res, 10));
	resetTeamNameAssets(editor, content);
	return scene;
}
